import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import './style.less';
import { Select, Space } from 'antd';
import UserAvatar from 'shared/components/UserAvatar';
import { useAppSelector } from 'shared/store';
import { selectActiveOrgMembers } from 'shared/store/auth-slice';

export interface DataPoint {
  label: string;
  value: React.ReactNode;
  valueString?: string;
  key?: string;
  editable?: boolean;
  type?: string;
}

type LabelValueListProps = {
  data: DataPoint[];
  onDataChange?: (newData: DataPoint[]) => void;
  header?: string;
};

export const LabelValueList: React.FC<LabelValueListProps> = ({
  data,
  onDataChange,
  header,
}) => {
  const handleValueChange = (index: number, newValue?: string | number | boolean) => {
    if (!onDataChange) return;
    const newPoint = data[index];
    newPoint.value = newValue;
    onDataChange([newPoint]);
  };

  return (
    <div className="LabelValueList">
      {header && <div className="LabelValueList__header">{header}</div>}
      {data.map((d, idx) => {
        return (
          <div className="LabelValueList__item" key={`${idx}-${d.label}`}>
            <div className="LabelValueList__label" title={d.label}>
              {d.label}
            </div>
            <div
              className="LabelValueList__value"
              title={d.valueString || d.value?.toString()}
            >
              {d.editable ? (
                <InlineEditable
                  dataPoint={d}
                  onBlur={newText => handleValueChange(idx, newText)}
                />
              ) : (
                d.value || '-'
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface InlineEditableProps {
  dataPoint: DataPoint;
  onBlur: (value: string | number | boolean | undefined) => void;
}

const InlineEditable: React.FC<InlineEditableProps> = ({ dataPoint, onBlur }) => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && isEditing) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleBlur = (value: string | number | boolean | undefined) => {
    setIsEditing(false);
    handleUpdateValue(value);
  };

  const handleUpdateValue = (newValue?: string | number | boolean) => {
    if (typeof newValue === 'number') {
      onBlur(newValue);
    } else if (typeof newValue === 'string') {
      const trimmedValue = newValue?.trim();
      if (!trimmedValue) {
        onBlur(undefined);
        return;
      }
      if (dataPoint.type === 'float') {
        onBlur(parseFloat(trimmedValue));
      } else if (dataPoint.type === 'integer') {
        onBlur(parseInt(trimmedValue));
      } else {
        onBlur(trimmedValue);
      }
    } else if (typeof newValue === 'boolean') {
      onBlur(newValue);
    } else {
      onBlur(undefined);
    }
  };

  return (
    <>
      {isEditing ? (
        <Input dataPoint={dataPoint} onBlur={handleBlur} />
      ) : (
        <div
          className="LabelValueList__value--editable"
          onClick={() => setIsEditing(true)}
        >
          <DisplayValue dataPoint={dataPoint} />
        </div>
      )}
    </>
  );
};

interface InputProps {
  dataPoint: DataPoint;
  onBlur: (value: string | number | boolean | undefined) => void;
  placeholder?: string;
}

const NormalInput: React.FC<InputProps> = ({ dataPoint, onBlur, placeholder }) => {
  const text = dataPoint.valueString ?? dataPoint?.value?.toString();
  let inputType;
  if (dataPoint.type === 'float' || dataPoint.type === 'integer') {
    inputType = 'number';
  } else {
    inputType = 'text';
  }
  const [value, setValue] = useState(text);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const { key } = event;
    const keys = ['Tab'];
    const enterKey = 'Enter';
    const allKeys = [...keys, enterKey];

    if (key === 'Escape') {
      onBlur(value);
      return;
    }

    if (allKeys.includes(key)) {
      const trimmedValue = value?.trim();
      if (!trimmedValue) {
        setValue(text);
      }
      onBlur(trimmedValue);
    }
  };

  return (
    <input
      ref={inputRef}
      type={inputType}
      value={value || ''}
      placeholder={placeholder}
      onChange={handleChange}
      onBlur={() => onBlur(value)}
      onKeyDown={handleKeyDown}
    />
  );
};

const UserSelect: React.FC<InputProps> = ({ dataPoint, onBlur }) => {
  const availableUsers = useAppSelector(selectActiveOrgMembers);
  const defaultValue = dataPoint.value as number | undefined;

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.LabelValueList__userSelect')) {
        onBlur(defaultValue || undefined);
      }
    },
    [defaultValue, onBlur],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <Select
      className="LabelValueList__userSelect"
      placeholder="Select user"
      defaultValue={defaultValue || undefined}
      onChange={v => onBlur(v !== 0 ? v : undefined)}
      open={true}
      onBlur={() => onBlur(defaultValue || undefined)}
      getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}
    >
      <Select.Option key={0} value={0}>
        -
      </Select.Option>
      {availableUsers.map(user => (
        <Select.Option key={user.id} value={user.id}>
          <Space>
            <UserAvatar user={user} size={40}></UserAvatar>
            {user.displayName}
          </Space>
        </Select.Option>
      ))}
    </Select>
  );
};

const BoolSelect: React.FC<InputProps> = ({ dataPoint, onBlur }) => {
  const defaultValue = dataPoint.value === undefined ? -1 : dataPoint.value ? 1 : 0;

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.LabelValueList__boolSelect')) {
        onBlur(defaultValue || undefined);
      }
    },
    [defaultValue, onBlur],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  function onChange(value: number) {
    let mappedValue: boolean | undefined;

    if (value === -1) {
      mappedValue = undefined;
    } else {
      mappedValue = Boolean(value);
    }

    onBlur(mappedValue);
  }

  return (
    <Select
      className="LabelValueList__boolSelect"
      placeholder="Select value"
      defaultValue={defaultValue || undefined}
      onChange={onChange}
      open={true}
      getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}
    >
      <Select.Option key={0} value={-1}>
        -
      </Select.Option>
      <Select.Option key={1} value={1}>
        Yes
      </Select.Option>
      <Select.Option key={2} value={0}>
        No
      </Select.Option>
    </Select>
  );
};

export const Input: React.FC<InputProps> = ({ dataPoint, onBlur, placeholder }) => {
  if (dataPoint.type === 'user') {
    return <UserSelect dataPoint={dataPoint} onBlur={onBlur} />;
  } else if (dataPoint.type === 'bool') {
    return <BoolSelect dataPoint={dataPoint} onBlur={onBlur} />;
  } else {
    return (
      <NormalInput dataPoint={dataPoint} onBlur={onBlur} placeholder={placeholder} />
    );
  }
};

const DisplayValue: React.FC<{ dataPoint: DataPoint }> = ({ dataPoint }) => {
  const displayValue = dataPoint.value;
  const activeUsers = useAppSelector(selectActiveOrgMembers);

  if (dataPoint.type === 'user') {
    const user = activeUsers.find(u => u.id === displayValue);
    if (!user) {
      return <>{'-'}</>;
    }
    return (
      <>
        <UserAvatar user={user} size={40} className="LabelValueList__avatar" />
        {user.displayName}
      </>
    );
  } else if (dataPoint.type === 'bool') {
    if (displayValue === undefined) {
      return <>{'-'}</>;
    } else if (displayValue) {
      return <>Yes</>;
    } else {
      return <>No</>;
    }
  } else {
    return <>{displayValue || '-'}</>;
  }
};

export default InlineEditable;
